import { z } from 'zod';

/**
 * Dual-mode runtime (public contract).
 *
 * - `ai` — free conversation → understanding → validation/CRM/rules → Decision Engine → LLM response
 * - `crm_fallback` — deterministic CRM wizard (directions → services → staff → slots → confirm)
 *
 * Invariants:
 * - Mode is decided only by the server; UI must never infer it from errors, buttons, fsmSnapshot, or reply text.
 * - Legacy FSM does not own LLM discovery dialogue.
 * - Booking transaction remains a separate protected flow (not this enum).
 * - Do not label rule-based/template replies as `crm_fallback` — that mode requires the CRM wizard runtime.
 *
 * Note: distinct from `conversation.mode: "intelligence"` (session memory / presentation bridge).
 */
export const conversationModeSchema = z.enum(['ai', 'crm_fallback']);
export type ConversationMode = z.infer<typeof conversationModeSchema>;

/**
 * Server-only reasons that may trigger `crm_fallback`.
 * Never use these strings as client UI copy; do not put PII in logs alongside them.
 */
/**
 * Слой обслуживания, на котором получен этот ответ.
 *
 * Ф6 завела три слоя, но наружу до сих пор уходил только двузначный `mode`, и
 * третий слой маскировался под `ai`. Из-за этого виджет не мог отличить
 * «ассистент отвечает» от «не работает ничего, вот телефон» и вынужден был бы
 * угадывать по паре косвенных признаков — той самой эвристике, от которой
 * Ф6 и Ф7 уходят.
 *
 * `mode` остаётся как есть: он часть публичного контракта и говорит, какой
 * **вид** сессии пришёл (у `crm_fallback` обязателен свой `crmFallback`).
 * `runtimeLayer` говорит другое — **кто отвечает**. Поля не дублируют друг
 * друга, и схема ниже сторожит их согласованность.
 *
 * Значения совпадают с `RuntimeLayer` ядра намеренно; за расхождением следит
 * отдельный тест, иначе получилось бы два словаря об одном и том же.
 */
export const conversationRuntimeLayerSchema = z.enum(['assistant', 'scripted', 'offline']);
export type ConversationRuntimeLayer = z.infer<typeof conversationRuntimeLayerSchema>;

export const crmFallbackReasonSchema = z.enum([
  'llm_timeout',
  'provider_unavailable',
  'invalid_structured_output',
  'circuit_breaker_open',
]);
export type CrmFallbackReason = z.infer<typeof crmFallbackReasonSchema>;

/** Parse public mode — returns null for missing/unknown values (no silent default to `ai`). */
export function parseConversationMode(value: unknown): ConversationMode | null {
  const parsed = conversationModeSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}
